import { useEffect, useRef } from 'react'

/**
 * Flips `data-revealed` on every `[data-reveal]` descendant as it enters the
 * viewport. The transition itself lives in CSS, so nothing runs per frame.
 *
 * By default each element reveals once and stays. Pass `{ once: false }` to
 * replay: the attribute is cleared when the element leaves the viewport, so
 * the animation runs again on every return.
 */
export function useReveal({ once = true } = {}) {
  const ref = useRef(null)

  useEffect(() => {
    const root = ref.current
    if (!root) return

    const targets = Array.from(root.querySelectorAll('[data-reveal]'))
    if (!targets.length) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', 'true')
            if (once) observer.unobserve(entry.target)
          } else if (!once) {
            entry.target.removeAttribute('data-revealed')
          }
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
    )

    targets.forEach((target) => observer.observe(target))
    return () => observer.disconnect()
  }, [once])

  return ref
}
